import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Plus, ArrowLeft, Shield, FileText, List as ListIcon, Columns, Settings, Archive, Trash2, X, AlertTriangle, ArchiveRestore, BookOpen, Pencil, Upload, Image as ImageIcon } from 'lucide-react';
import { absoluteMediaUrl } from '../media';
import DesignTaskCreateWizard from './DesignTaskCreateWizard';
import DesignTaskDetail from './DesignTaskDetail';
import DesignTaskKanban from './DesignTaskKanban';
import './DesignTaskProject.css';

const STATE_NAMES = {
  1:  'Initiated', 2:  'Classified', 3:  'Impact Assessment', 4:  'Drafting',
  5:  'Technical Review', 6:  'Independent Verification', 7:  'Configuration Update',
  8:  'Ready for Release', 9:  'Released', 10: 'Closed', 11: 'Cancelled',
};

const STATE_TINT = {
  1: 'todo', 2: 'todo', 3: 'progress', 4: 'progress', 5: 'progress',
  6: 'progress', 7: 'progress', 8: 'progress', 9: 'done', 10: 'done', 11: 'muted',
};

// localStorage key for the user's preferred default view.
// Persisted client-side per browser; survives navigation to a DT detail
// and back, refreshes, and tab restarts.
const VIEW_STORAGE_KEY = 'kds.dt.projectView';

function loadStoredView() {
  if (typeof window === 'undefined' || !window.localStorage) return 'list';
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return v === 'kanban' ? 'kanban' : 'list';
  } catch (_) {
    return 'list';
  }
}

function persistView(v) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try { localStorage.setItem(VIEW_STORAGE_KEY, v); } catch (_) { /* quota etc. — ignore */ }
}

function DesignTaskProject({ user }) {
  return (
    <Routes>
      <Route path="/" element={<DesignTaskBoardOrList user={user} />} />
      <Route path="dt/:dtId" element={<DesignTaskDetail user={user} />} />
    </Routes>
  );
}

function DesignTaskBoardOrList({ user }) {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [view, setViewState] = useState(loadStoredView);

  const [project, setProject] = useState(null);
  const [dts, setDts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, dtRes] = await Promise.all([
        axios.get(`/projects/${projectId}`),
        axios.get(`/projects/${projectId}/designtasks`),
      ]);
      setProject(pRes.data);
      setDts(dtRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load Design Tasks.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreated = (dt) => {
    setShowCreate(false);
    setDts(prev => [dt, ...prev]);
    navigate(`/project/${projectId}/dt/${dt._id}`);
  };

  const setView = (newView) => {
    setViewState(newView);
    persistView(newView);
  };

  const isOwner = project && String(project.owner?._id || project.owner) === String(user._id);

  const handleDelete = async () => {
    setBusy(true);
    try {
      await axios.delete(`/projects/${projectId}`);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete project.');
      setBusy(false);
      setShowDelete(false);
    }
  };

  const handleArchive = async () => {
    setBusy(true);
    try {
      if (project.archived) {
        await axios.post(`/projects/${projectId}/unarchive`);
      } else {
        await axios.post(`/projects/${projectId}/archive`);
      }
      setShowArchive(false);
      setShowSettings(false);
      // Send the user back to the project list — archived projects are
      // hidden there by default, so this reinforces the action took effect.
      if (!project.archived) {
        navigate('/');
      } else {
        fetchAll();
        setBusy(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change archive state.');
      setBusy(false);
    }
  };

  const handleEditProject = async ({ name, description, color, imageFile, removeImage }) => {
    try {
      // 1. Save text fields if changed
      const updates = {};
      if (name !== undefined && name !== project.name) updates.name = name;
      if (description !== undefined && description !== (project.description || '')) updates.description = description;
      if (color !== undefined && color !== project.color) updates.color = color;
      if (Object.keys(updates).length) {
        await axios.put(`/projects/${projectId}`, updates);
      }

      // 2. Image: upload new, remove existing, or no-op
      if (removeImage) {
        await axios.put(`/projects/${projectId}/image`, { image: '' });
      } else if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        await axios.put(`/projects/${projectId}/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setShowEdit(false);
      fetchAll();
    } catch (err) {
      throw err;
    }
  };

  if (loading) return (
    <div className="loading-screen" style={{ minHeight: 'auto', padding: '60px 0' }}>
      <div className="loading-spinner"></div>
    </div>
  );

  return (
    <div className="dt-project">
      <header className="dt-project-header">
        <button className="dt-back" onClick={() => navigate('/')} title="All projects">
          <ArrowLeft size={16} />
        </button>
        <div className="dt-project-title-wrap">
          <div className="dt-project-eyebrow">
            <Shield size={12} /> Design Assurance project
            {project?.archived && (
              <span className="dt-project-archived-tag">· Archived</span>
            )}
          </div>
          <h1 className="dt-project-title">{project?.name}</h1>
          {project?.description && (
            <p className="dt-project-description">{project.description}</p>
          )}
        </div>
        <div className="dt-project-actions">
          <button
            className="dt-btn-ghost"
            onClick={() => navigate(`/project/${projectId}/folios`)}
            title="Project folios"
          >
            <BookOpen size={14} /> Folios
          </button>
          {isOwner && (
            <div className="dt-project-settings">
              <button
                className="dt-btn-icon"
                onClick={() => setShowSettings(s => !s)}
                title="Project settings"
                aria-label="Project settings"
              >
                <Settings size={16} />
              </button>
              {showSettings && (
                <>
                  <div className="dt-project-settings-backdrop" onClick={() => setShowSettings(false)}></div>
                  <div className="dt-project-settings-menu">
                    <button
                      className="dt-project-settings-item"
                      onClick={() => { setShowSettings(false); setShowEdit(true); }}
                    >
                      <Pencil size={14} /> Edit project details
                    </button>
                    <button
                      className="dt-project-settings-item"
                      onClick={() => { setShowSettings(false); setShowArchive(true); }}
                    >
                      {project?.archived ? <><ArchiveRestore size={14} /> Unarchive project</> : <><Archive size={14} /> Archive project</>}
                    </button>
                    <button
                      className="dt-project-settings-item dt-project-settings-item-danger"
                      onClick={() => { setShowSettings(false); setShowDelete(true); }}
                    >
                      <Trash2 size={14} /> Delete project permanently
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          <button className="dt-btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> New Design Task
          </button>
        </div>
      </header>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {/* View toggle — only shown if there's at least one DT */}
      {dts.length > 0 && (
        <div className="dt-view-toggle">
          <button
            className={`dt-view-btn ${view === 'list' ? 'is-active' : ''}`}
            onClick={() => setView('list')}
            aria-pressed={view === 'list'}
          >
            <ListIcon size={13} /> List
          </button>
          <button
            className={`dt-view-btn ${view === 'kanban' ? 'is-active' : ''}`}
            onClick={() => setView('kanban')}
            aria-pressed={view === 'kanban'}
          >
            <Columns size={13} /> Kanban
          </button>
        </div>
      )}

      {dts.length === 0 ? (
        <div className="dt-empty">
          <FileText size={36} strokeWidth={1.5} />
          <h3>No Design Tasks yet</h3>
          <p>Raise the first controlled work item to start the procedure.</p>
          <button className="dt-btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> New Design Task
          </button>
        </div>
      ) : view === 'kanban' ? (
        <DesignTaskKanban dts={dts} />
      ) : (
        <div className="dt-list">
          <div className="dt-list-header">
            <span>Identifier</span>
            <span>Title</span>
            <span>State</span>
            <span>Urgency</span>
            <span>Scope</span>
            <span>Raised</span>
          </div>
          {dts.map(dt => (
            <button
              key={dt._id}
              className="dt-list-row"
              onClick={() => navigate(`/project/${projectId}/dt/${dt._id}`)}
            >
              <span className="dt-ident">{dt.identifier}</span>
              <span className="dt-title-cell">{dt.title}</span>
              <span className={`dt-state-pill dt-state-${STATE_TINT[dt.procedureState]}`}>
                {dt.procedureState}. {STATE_NAMES[dt.procedureState]}
              </span>
              <span className={`dt-urgency dt-urgency-${dt.urgency}`}>{dt.urgency}</span>
              <span className="dt-scope">
                {dt.scopeType === 'initial-creation'  && 'Initial creation'}
                {dt.scopeType === 'change-to-released' && 'Change'}
                {dt.scopeType === 'hybrid'             && 'Hybrid'}
              </span>
              <span className="dt-date">
                {dt.createdAt && new Date(dt.createdAt).toLocaleDateString()}
              </span>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <DesignTaskCreateWizard
          projectId={projectId}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {showArchive && (
        <ArchiveProjectModal
          project={project}
          dtCount={dts.length}
          busy={busy}
          onClose={() => setShowArchive(false)}
          onConfirm={handleArchive}
        />
      )}

      {showEdit && (
        <EditProjectModal
          project={project}
          onClose={() => setShowEdit(false)}
          onSave={handleEditProject}
        />
      )}

      {showDelete && (
        <DeleteProjectModal
          project={project}
          dtCount={dts.length}
          busy={busy}
          onClose={() => setShowDelete(false)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Archive / Unarchive confirmation
// ─────────────────────────────────────────────────────────────────

function ArchiveProjectModal({ project, dtCount, busy, onClose, onConfirm }) {
  const isUnarchiving = !!project?.archived;
  return (
    <div className="dt-modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow">{isUnarchiving ? 'Unarchive project' : 'Archive project'}</p>
            <h2>{project?.name}</h2>
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close" disabled={busy}>
            <X size={18} />
          </button>
        </header>
        <div className="dt-modal-body">
          {isUnarchiving ? (
            <p className="dt-confirm-text">
              Restore <code className="dt-confirm-id">{project?.name}</code> to the active project list.
              All {dtCount} Design Task{dtCount === 1 ? '' : 's'} and audit records are intact.
            </p>
          ) : (
            <>
              <p className="dt-confirm-text">
                Hide <code className="dt-confirm-id">{project?.name}</code> from the active project list.
                The project's {dtCount} Design Task{dtCount === 1 ? '' : 's'} and audit records stay intact —
                you can restore them anytime by unarchiving.
              </p>
              <p className="dt-confirm-hint">
                Use Archive for completed work you want to declutter from view. Use Delete (separate option)
                if the project was raised in error and you want it gone permanently.
              </p>
            </>
          )}
        </div>
        <div className="dt-modal-actions">
          <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="dt-btn-primary" onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : (isUnarchiving ? 'Unarchive' : 'Archive project')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Delete project confirmation — requires typing the project name
// ─────────────────────────────────────────────────────────────────

function DeleteProjectModal({ project, dtCount, busy, onClose, onConfirm }) {
  const [typed, setTyped] = useState('');
  const canDelete = typed.trim() === project?.name?.trim();
  return (
    <div className="dt-modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow" style={{ color: 'var(--accent)' }}>Permanent action</p>
            <h2>Delete project?</h2>
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close" disabled={busy}>
            <X size={18} />
          </button>
        </header>
        <div className="dt-modal-body">
          <p className="dt-confirm-text">
            This will permanently delete <code className="dt-confirm-id">{project?.name}</code>,
            along with all <strong>{dtCount} Design Task{dtCount === 1 ? '' : 's'}</strong>,
            their assignments, audit logs, evidence items, baselines, and the identifier counter
            <em> for this project</em>. <strong>This cannot be undone.</strong>
          </p>
          <p className="dt-confirm-hint">
            For completed projects you just want out of view, Archive instead — same project,
            preserved data, just hidden from the list.
          </p>
          <div className="dt-field" style={{ marginTop: 14 }}>
            <label htmlFor="dp-confirm">
              Type <code style={{ color: 'var(--accent)' }}>{project?.name}</code> to confirm
            </label>
            <input
              id="dp-confirm"
              type="text"
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder={project?.name}
              autoFocus
            />
          </div>
        </div>
        <div className="dt-modal-actions">
          <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={busy}>
            Keep project
          </button>
          <button
            type="button"
            className="dt-btn-danger"
            onClick={onConfirm}
            disabled={!canDelete || busy}
          >
            {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Edit project details — name, description, color, image
// ─────────────────────────────────────────────────────────────────

const PROJECT_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#ef4444',
  '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6',
];

function EditProjectModal({ project, onClose, onSave }) {
  const [name, setName] = useState(project.name || '');
  const [description, setDescription] = useState(project.description || '');
  const [color, setColor] = useState(project.color || PROJECT_COLORS[0]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  // WR-018 §2 sweep: was an unsigned `origin + project.image`, which 401s
  // since WR-014 made /uploads private. absoluteMediaUrl signs it.
  const currentImageUrl = project.image && !removeImage && !imagePreview
    ? absoluteMediaUrl(project.image)
    : null;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErr('Pick an image file (jpg, png, webp).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErr('Image must be under 5 MB.');
      return;
    }
    setErr('');
    setImageFile(file);
    setRemoveImage(false);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        color,
        imageFile,
        removeImage,
      });
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Failed to save changes.');
      setSubmitting(false);
    }
  };

  return (
    <div className="dt-modal-backdrop" onClick={submitting ? undefined : onClose}>
      <div className="dt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow">Edit project</p>
            <h2>{project.name}</h2>
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close" disabled={submitting}>
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="dt-wizard-form">
          <div className="dt-field">
            <label htmlFor="ep-name">Name *</label>
            <input
              id="ep-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
            <span className="dt-field-hint">
              The project name is part of the DT identifier; changing it after DTs exist may affect display.
            </span>
          </div>

          <div className="dt-field">
            <label htmlFor="ep-desc">Description</label>
            <textarea
              id="ep-desc"
              rows={2}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Short summary of what this project covers."
            />
          </div>

          <div className="dt-field">
            <label>Thumbnail image</label>
            {(currentImageUrl || imagePreview) ? (
              <div className="dt-image-preview-wrap">
                <div
                  className="dt-image-preview"
                  style={{ backgroundImage: `url(${imagePreview || currentImageUrl})` }}
                  role="img"
                  aria-label="Project thumbnail"
                />
                <div className="dt-image-actions">
                  <label className="dt-image-btn">
                    <Upload size={12} /> Replace
                    <input type="file" accept="image/*" onChange={handleFile} hidden />
                  </label>
                  <button type="button" className="dt-image-btn dt-image-btn-danger" onClick={handleRemoveImage}>
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
            ) : (
              <label className="dt-image-upload-empty">
                <ImageIcon size={28} />
                <span>Click to upload a thumbnail</span>
                <span className="dt-image-upload-hint">PNG or JPG · up to 5 MB</span>
                <input type="file" accept="image/*" onChange={handleFile} hidden />
              </label>
            )}
          </div>

          <div className="dt-field">
            <label>Color</label>
            <div className="dt-color-swatches">
              {PROJECT_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  className={`dt-color-swatch ${color === c ? 'is-selected' : ''}`}
                  style={{ background: c }}
                  onClick={() => setColor(c)}
                  aria-label={`Set color ${c}`}
                />
              ))}
            </div>
          </div>

          {err && <div className="error-message">{err}</div>}

          <div className="dt-modal-actions">
            <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="dt-btn-primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DesignTaskProject;
